import { CancellableRequest } from '@services/HttpService'
import isEqual from 'lodash/isEqual'
import debounce from 'lodash/debounce'

const defaultConfig = {
  wait: 2000,
  useLeading: true,
  maxWait: undefined
}

export type DebouncePoolConfig = {
  wait?: number
  useLeading?: boolean
  maxWait?: number
}

export type DPAddConfig = {
  /** Any string representing a call site, can be general or unique as appropriate. */
  key: string

  /**
   * Escape hatch that allows you to cancel the debounce timer when a same or different key is passed
   * and the queue is idle. When all async functions have finished, an add() within the wait period will
   * normally debounce otherwise. An example is a checkbox that you don't want to debounce if the user
   * checks and unchecks it repeatedly, but you do want it to debounce and queue with other actions.
   */
  resetTimerIf?: (previousKey: string) => boolean
}

export type DPAddCompareConfig<A extends any[]> = DPAddConfig & {
  /**
   * Advanced: for normal pooling in which calls with the same arguments are pooled, a boolean return
   * here indicates whether to pool the call (true) or not (false). If this isn't provided, `isEqual`
   * is used (deep-compare), so defining this may help improve efficiency by directly inspecting the
   * most important details indicating a match.
   * @param args1 Arguments passed to the current call
   * @param args2 Arguments passed to a previous call with the same `key`
   */
  compareFn: (args1: A, args2: A) => boolean
}

export type DPAddModifyConfig<A extends any[]> = DPAddConfig & {
  /**
   * Use to combine or modify arguments that will be used in the pooled call (e.g. to condense IDs),
   * or return false to not pool the call.
   *
   * WARNING! Tampers with awaited results at non-primary call sites (results will no longer match the
   * arguments passed), so when debugging and handling the response only honor the `isPrimaryResponse`
   * case. (If this causes problems we could reject non-primary calls with a custom error type instead)
   *
   * @param args1 Arguments passed to the current call
   * @param args2 Arguments passed to a previous call with the same `key`
   * @returns Modified arguments to use in the pooled call, or false to not pool the call
   */
  modifyFn?: (args1: A, args2: A) => A | false
}

export type DebouncePoolResult<R> = {
  response: R
  poolSize: number
  isPrimaryResponse: boolean
  calledArgs: any[]
}

type AsyncFn<R, A extends any[]> = (...args: A) => AsyncResult<R>

type AsyncResult<T> = Promise<T> | CancellableRequest<T>

type Task<R, A extends any[]> = {
  key: string
  fn: AsyncFn<R, A>
  args: A
  resolves: Array<(value: DebouncePoolResult<R>) => void>
  rejects: Array<(reason?: any) => void>
}

/**
 * Pools duplicate requests after a debounce wait.
 */
export class DebouncePool {
  /**
   * @param wait Default debounce wait time in milliseconds
   * @param useLeading Recommended to always use this setting to ensure that the first caller is
   * not delayed by the debounce period. This keeps the UI responsive on single actions.
   */
  public constructor(config: DebouncePoolConfig = defaultConfig) {
    this.debouncedDrainQueue = debounce(this.drainQueue, config.wait, {
      leading: config.useLeading,
      trailing: true,
      maxWait: config.maxWait
    })
  }

  private debouncedDrainQueue: ReturnType<typeof debounce>
  private readonly queue: Array<Task<any, any[]>> = []
  private state: 'idle' | 'debounce' | 'drain' = 'idle'
  private lastKey?: string

  // Overload for plain functions with no arguments
  /**
   * Adds an async function to the pool, using `key` to eliminate duplicates.
   *
   * The returned object contains:
   * - `response`: async function result (shared among pooled callers, so treat as read-only)
   * - `poolSize`: the number of callers for the same function
   * - `isPrimaryResponse`: whether the current caller was the last one added to the pool
   *
   * @param config Object containing `key`
   * @param fn Async function, Mobx action, or Axios call
   * @returns Promise resolving to DebouncePoolResult
   */
  public add<R>(
    config: DPAddConfig,
    fn: () => AsyncResult<R>,
    ...args: []
  ): Promise<DebouncePoolResult<R>>

  // Overload for functions with arguments, args are code-hinted
  /**
   * Adds an async function to the pool, using `key` and arguments to eliminate duplicates.
   *
   * The returned object contains:
   * - `response`: async function result (shared among pooled callers, so treat as read-only)
   * - `poolSize`: the number of callers for the same function and arguments
   * - `isPrimaryResponse`: whether the current caller was the last one added to the pool
   *
   * @example
   * ```
   * private debouncePool = new DebouncePool()
   *
   * const { response, isPrimaryResponse } = await this.debouncePool.add(
   *   {key: 'getUser.fetchCurrentUser'},
   *   service.fetchCurrentUser,
   *   userId
   * )
   * if (!isPrimaryResponse) {
   *   return // Do complex work only once to avoid duplicate effort
   * }
   * updateUserState(response)
   * ```
   *
   * @param config Object containing `key` and optional `compareFn` for custom argument compare or combine
   * @param fn Async function, Mobx action, or Axios call
   * @param args Arguments passed fn when called
   * @returns Promise resolving to DebouncePoolResult
   */
  public add<R, A extends any[]>(
    config: DPAddConfig | DPAddCompareConfig<A> | DPAddModifyConfig<A>,
    fn: AsyncFn<R, A>,
    ...args: A
  ): Promise<DebouncePoolResult<R>>

  public add<R, A extends any[]>(
    config: DPAddConfig | DPAddCompareConfig<A> | DPAddModifyConfig<A>,
    fn: AsyncFn<R, A>,
    ...args: A
  ): Promise<DebouncePoolResult<R>> {
    if (this.state === 'idle' && this.lastKey && config.resetTimerIf?.(this.lastKey)) {
      // console.log(`DebouncePool: timer reset triggered (idle, ${config.resetTimerIf})`)
      this.debouncedDrainQueue.cancel()
    }

    this.lastKey = config.key
    return new Promise((resolve, reject) => {
      let match = false
      for (const task of this.queue) {
        if (task.key === config.key) {
          if ('modifyFn' in config && config.modifyFn) {
            const modified = config.modifyFn(args, task.args as A)
            if (modified) {
              // console.log(`DebouncePool: modifying args for key ${config.key}`)
              task.args = modified
              task.resolves.push(resolve)
              task.rejects.push(reject)
              match = true
              break
            }
          }

          const compareFn =
            'compareFn' in config && config.compareFn ? config.compareFn : isEqual
          if (compareFn(args, task.args as A)) {
            // console.log(`DebouncePool: pooling call for key ${config.key}`)
            task.resolves.push(resolve)
            task.rejects.push(reject)
            task.args = args // since they may be using a key-based rather than args-based match, so args can differ
            match = true
            break
          }
        }
      }

      if (!match) {
        // console.log(`DebouncePool: adding new task for key ${config.key}`)
        const newTask: Task<R, A> = {
          key: config.key,
          fn: fn,
          args,
          resolves: [resolve],
          rejects: [reject]
        }
        this.queue.push(newTask)
      }

      this.state = 'debounce'
      this.debouncedDrainQueue()
    })
  }

  /* tabling for now - was locking up the app even with errors caught (but not rejecting may cause a memory leak). Needs stress-testing
  public remove(key: string) {
    let index: number
    while ((index = this.queue.findIndex(task => task.key === key)) > -1) {
      const removedTask = this.queue.splice(index, 1)[0]
      removedTask.rejects.forEach(reject => {
        reject(new Error('DebouncePool task was removed'))
      })
    }
  } */

  public has(key: string) {
    return this.queue.some(task => task.key === key)
  }

  /**
   * Rejects all active tasks and clears the queue asynchronously to avoid PromiseRejectionHandledWarning.
   */
  public clear() {
    if (this.queue.length > 0) {
      this.debouncedDrainQueue.cancel()
      const tasksToReject = this.queue.splice(0, this.queue.length)
      console.warn(
        `this.clear(): ${
          tasksToReject.length
        } canceled promise(s) will be rejected - keys: ${tasksToReject
          .map(task => task.key)
          .join(', ')}`
      )
      this.rejectTasks(tasksToReject)
    }
  }

  // -- Private methods --

  async drainQueue() {
    if (this.state === 'drain') {
      return
    }
    this.state = 'drain'
    // console.log(`DebouncePool: draining queue with ${this.queue.length} tasks`)
    // Series execution is intentional! It's a bit slower but preserves data integrity and mitigates concurrency issues on the services.
    // (We could potentially add a `concurrent` config option if needed for special cases where response order doesn't matter.)
    while (this.queue.length > 0) {
      const task = this.queue.shift()
      const fn = task.fn
      // console.log(`DebouncePool: calling function for key ${task.key}`)
      try {
        const result = await fn(...task.args)
        task.resolves.forEach((resolve, index) => {
          const response: DebouncePoolResult<any> = {
            response: result,
            poolSize: task.resolves.length,
            isPrimaryResponse: index === task.resolves.length - 1,
            calledArgs: task.args
          }
          resolve(response)
        })
        if (task.resolves.length > 1) {
          console.log(`DebouncePool: pooled ${task.resolves.length} calls '${task.key}'`)
        }
      } catch (error) {
        task.rejects.forEach(reject => reject(error))
      }
    }
    this.state = 'idle'
  }

  // This is broken out so it can run asynchronously after clear()
  private async rejectTasks(tasks: Array<Task<any, any[]>>) {
    try {
      await Promise.all(
        tasks.flatMap(task =>
          task.rejects.map(reject => reject(new Error('DebouncePool was cleared')))
        )
      )
    } catch {
      console.error('DebouncePool: Error rejecting promises during clear.')
    }
  }
}
